import { computed } from 'vue'
import { useOrganizationStore } from '@/stores/organization'

/**
 * Composable for license-based feature access control (ADR-023).
 *
 * Provides reactive computed properties that indicate which features
 * are available based on the organization's license type.
 *
 * Usage:
 * ```vue
 * <script setup>
 * import { useFeatureAccess } from '@/composables/useFeatureAccess'
 *
 * const { isFormsOnly, canLinkToClients, availablePermissions } = useFeatureAccess()
 * </script>
 *
 * <template>
 *   <div v-if="canLinkToClients">
 *     <ClientSelector />
 *   </div>
 * </template>
 * ```
 */
export function useFeatureAccess() {
  const organizationStore = useOrganizationStore()

  const licenseType = computed(() =>
    organizationStore.organization?.licenseType ?? 'FULL'
  )

  const isFormsOnly = computed(() => licenseType.value === 'FORMS_ONLY')
  const canLinkToClients = computed(() => licenseType.value === 'FULL')
  const canLinkToAppointments = computed(() => licenseType.value === 'FULL')
  const canCustomizeRoles = computed(() => licenseType.value === 'FULL')

  /**
   * The set of permission keys available for this organization's license.
   * Returns an empty array if organization data is not loaded yet.
   */
  const availablePermissions = computed(
    () => organizationStore.organization?.availablePermissions ?? []
  )

  /**
   * Checks if a specific permission is available for this organization's license.
   * @param permissionKey The permission key to check (e.g., 'clients.read')
   * @returns True if the permission is available
   */
  function isPermissionAvailable(permissionKey: string): boolean {
    return availablePermissions.value.includes(permissionKey)
  }

  return {
    licenseType,
    isFormsOnly,
    canLinkToClients,
    canLinkToAppointments,
    canCustomizeRoles,
    availablePermissions,
    isPermissionAvailable,
  }
}
